/**
 * The persistent edit island, sticky at the bottom of the viewport so the controls never scroll
 * away from the bars they act on. History (undo/redo/revert), the selection readout, the edit ops
 * (merge/split/set-stroke/clear) and pool length. Buttons disable with a reason from the domain
 * preconditions (canMerge/canSplit). All actions are already on the store.
 */
import { store } from '../state/store'
import { activeOrdinals, canMerge, canSplit } from '../domain/edit'
import { splitParts, STROKES, SELECTED_COLOR } from './uiState'

export function EditIsland() {
  const state = store.currentState.value
  const selection = [...store.selection.value].sort((a, b) => a - b)
  if (!state) return null

  const merge = canMerge(state, selection)
  const splitOrdinal = selection.length === 1 ? selection[0]! : null
  const split =
    splitOrdinal === null
      ? { ok: false, reason: 'Select exactly one length' }
      : canSplit(state, splitOrdinal, splitParts.value)

  const hasSelection = selection.length > 0
  const strokeTargets = hasSelection ? selection : activeOrdinals(state)
  const strokeScope = hasSelection ? 'selection' : 'all'

  return (
    <div class="island">
      <div class="row">
        <button
          class="btn"
          disabled={!store.canUndo.value}
          onClick={() => store.undo()}
          title="Undo"
        >
          ↶ Undo
        </button>
        <button
          class="btn"
          disabled={!store.canRedo.value}
          onClick={() => store.redo()}
          title="Redo"
        >
          ↷ Redo
        </button>
        <button
          class="btn"
          disabled={!store.canReset.value}
          title="Discard all edits and revert to the original"
          onClick={() => {
            if (confirm('Discard all edits and revert to the original file?')) store.reset()
          }}
        >
          Revert all
        </button>
      </div>

      <div class="divider" />

      <div class="sel">
        <span class="dot" style={`background:${hasSelection ? SELECTED_COLOR : '#c7c7cc'}`} />
        <span style={hasSelection ? '' : 'color:var(--ink-3)'}>
          {hasSelection ? `${selection.length} lengths selected` : 'Nothing selected'}
        </span>
      </div>

      <button
        class="btn"
        disabled={!merge.ok}
        title={merge.reason ?? 'Merge selected lengths'}
        onClick={() => store.merge(selection)}
      >
        Merge
      </button>

      <div class="row">
        <button
          class="btn"
          disabled={!split.ok}
          title={split.reason ?? 'Split the selected length'}
          onClick={() => store.split(splitOrdinal!, splitParts.value)}
        >
          Split into
        </button>
        <input
          type="number"
          min={2}
          max={10}
          value={splitParts.value}
          style="width:48px"
          onInput={(e) => (splitParts.value = Number((e.target as HTMLInputElement).value))}
          aria-label="Split into N parts"
        />
      </div>

      <select
        aria-label="Set stroke"
        value=""
        onChange={(e) => {
          const el = e.target as HTMLSelectElement
          store.setStroke(strokeTargets, Number(el.value))
          el.value = ''
        }}
      >
        <option value="" disabled>
          Set stroke ({strokeScope})…
        </option>
        {STROKES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>

      <button
        class="btn btn-ghost"
        onClick={() =>
          hasSelection ? store.clearSelection() : store.setSelection(activeOrdinals(state))
        }
      >
        {hasSelection ? 'Clear' : 'Select all'}
      </button>

      <div class="grow" />

      <div class="row">
        <span class="muted" style="font-size:12px">
          Pool
        </span>
        <div class="seg">
          <button
            class={`seg-opt${store.poolLengthCm.value === 2500 ? ' on' : ''}`}
            onClick={() => store.setPool(2500)}
          >
            25 m
          </button>
          <button
            class={`seg-opt${store.poolLengthCm.value === 5000 ? ' on' : ''}`}
            onClick={() => store.setPool(5000)}
          >
            50 m
          </button>
        </div>
        <input
          type="number"
          min={1}
          max={655}
          step="0.1"
          placeholder="Custom"
          style="width:82px"
          onChange={(e) => {
            const m = Number((e.target as HTMLInputElement).value)
            if (m > 0) store.setPool(Math.round(m * 100))
          }}
          aria-label="Custom pool length in metres"
        />
      </div>
    </div>
  )
}
